/**
 * Dashboard ——「邹城中考志愿填报工具」公益项目主页
 *
 * 设计原则（用户审定）：
 *  - 5 栏目章节式布局（学院公益风），每个栏目编号 01–05，serif 大标题 + 细线分隔
 *  - 栏目顺序固定，按"中考全周期"自然展开：
 *      01 历年录取信息（已发生的事实）
 *      02 分数 → 学校 录取分析（核心工具）
 *      03 考前必读（备考期内容）
 *      04 高中适应预警（录取后内容）
 *      05 假期报班必读（暑期内容）
 *  - 阶段感知：通过顶部 StageGuidance 卡片告诉家长"当下你应该重点看哪个栏目"，
 *    而不是用阶段去隐藏 / 显示栏目（所有栏目永远可见，保持工具完整性）
 *
 *  - 未测算态：完整 5 栏目展示
 *  - 已测算态：栏目 02 的位置展开为详细结果仪表盘（PositionSection + SchoolsSection + …），
 *    其它栏目（01/03/04/05）保留，作为补充阅读
 */
import { useApp } from '@/store/useApp'
import type { CalcResult, YearPolicy } from '@/domain/types'
import type { StageInfo } from '@/domain/stage'
import { HeroBanner } from '@/components/HeroBanner'
import { StickyNav } from '@/components/StickyNav'
import { SectionHeader } from '@/components/SectionHeader'
import { StageGuidance } from '@/components/StageGuidance'
import { ScoreAnalysisSection } from '@/components/ScoreAnalysisSection'
import { ArticleStream } from '@/components/ArticleStream'
import { HistoricalSection } from '@/components/HistoricalSection'
import { AlgorithmSection } from '@/components/AlgorithmSection'
import { PositionSection } from '@/components/PositionSection'
import { SchoolsSection } from '@/components/SchoolsSection'
import { HeatSlidersSection } from '@/components/HeatSlidersSection'
import { SuggestionSection } from '@/components/SuggestionSection'
import { ConversionCards } from '@/components/ConversionCards'
import { BackfillPledge } from '@/components/BackfillPledge'
import { FollowOACard } from '@/components/FollowOACard'
import { PeakNotice } from '@/components/HeatStrip'
import { CATEGORY_LABEL } from '@/data/articles'
import { Footer } from '@/components/Footer'

export function Dashboard({
  stageInfo,
  policy,
  result
}: {
  stageInfo: StageInfo
  policy: YearPolicy
  result: CalcResult | null
}) {
  const { setWizardStep, setScore, setIntent } = useApp()

  return (
    <div className="bg-paper-50 min-h-screen pb-16">
      <HeroBanner stageInfo={stageInfo} />

      {/* 栏目导航 · 滚动时吸顶 */}
      <StickyNav />

      <main className="container-mobile py-6 space-y-10">
        {/* 高峰保护提示（仅 heat / queue 档展示，不阻断流程） */}
        <PeakNotice />

        {/* 当前阶段一句话指引 */}
        <StageGuidance stageInfo={stageInfo} hasResult={!!result} />

        {/* ── 01 邹城市中考志愿填报（核心工具）── */}
        <section>
          <SectionHeader
            num="01"
            title="邹城市中考志愿填报"
            subtitle="ZSM-1.1 模型 · 凝聚态物理博士姜博士主导设计"
            anchor="section-01"
          />
          {result ? (
            <ResultDashboard
              result={result}
              policy={policy}
              stageInfo={stageInfo}
              onReset={() => {
                setScore(null)
                setIntent(null)
                setWizardStep('score')
              }}
            />
          ) : (
            <ScoreAnalysisSection
              stageInfo={stageInfo}
              onCalculate={() => setWizardStep('score')}
            />
          )}
          <div className="mt-4">
            <AlgorithmSection />
          </div>
        </section>

        {/* ── 02 历年录取数据 ── */}
        <section>
          <SectionHeader
            num="02"
            title="历年录取数据"
            subtitle="资格线 · 前 300 线 · 五校招生比例 · 十分段表（近 4 年）"
            anchor="section-02"
          />
          <HistoricalSection />
        </section>

        {/* ── 03 考前必读 ── */}
        <CategorySection num="03" category="before_exam" />

        {/* ── 04 高中适应预警 ── */}
        <CategorySection num="04" category="high_school" />

        {/* ── 05 假期报班必读 ── */}
        <CategorySection num="05" category="summer_class" />

        {/* ── 关注公众号 ── */}
        <FollowOACard stage={stageInfo.stage} />
      </main>

      <Footer />
    </div>
  )
}

/** 已测算状态下，栏目 02 展开为完整结果仪表盘 */
function ResultDashboard({
  result,
  policy,
  stageInfo,
  onReset
}: {
  result: CalcResult
  policy: YearPolicy
  stageInfo: StageInfo
  onReset: () => void
}) {
  return (
    <div className="space-y-5">
      <PositionSection result={result} policy={policy} />
      <SchoolsSection result={result} />
      <SuggestionSection result={result} />
      <BackfillPledge result={result} stageInfo={stageInfo} />
      <ConversionCards result={result} />
      <HeatSlidersSection />
      <div className="card flex items-center justify-between">
        <div>
          <div className="text-ink-800">换一个分数测算？</div>
          <div className="text-ink-500 text-sm">可重新输入新的分数</div>
        </div>
        <button onClick={onReset} className="btn-ghost">
          重新测算
        </button>
      </div>
    </div>
  )
}

/** 文章栏目（03 / 04 / 05）—— 标题 + 文章流 */
function CategorySection({
  num,
  category
}: {
  num: string
  category: 'before_exam' | 'high_school' | 'summer_class'
}) {
  const meta = CATEGORY_LABEL[category]
  return (
    <section>
      <SectionHeader
        num={num}
        title={meta.title}
        subtitle={meta.subtitle}
        anchor={`section-${num}`}
      />
      <ArticleStream category={category} />
    </section>
  )
}
